import type { Volume } from '../types/product';

export const volumes: Volume[] = [
  {
    numeral: 'I',
    accent: 'blue',
    title: 'The ECG Foundations & Cardiac Electrical Activity Guide',
    headline: 'Understand What an ECG Actually Represents Before Trying to Interpret Complex Patterns',
    intro: [
      'Everything begins with the fundamentals.',
      'Before studying abnormal rhythms or 12-lead patterns, it helps to understand how electrical activity travels through the heart and how that activity appears on an ECG tracing.',
      'This volume organizes the essential concepts into a clear educational reference.',
    ],
    items: [
      'Introduction to electrocardiography',
      'Basic cardiac anatomy and physiology',
      'The cardiac electrical conduction system',
      'Sinoatrial and atrioventricular node concepts',
      'Depolarization and repolarization fundamentals',
      'Understanding ECG paper and calibration',
      'Heart rate calculation methods',
      'Identifying P waves, QRS complexes, and T waves',
      'PR interval and QRS duration fundamentals',
      'QT and corrected QT concepts',
      'Introduction to ECG lead placement',
      'Common sources of ECG artifacts',
      'Foundational review exercises',
    ],
    takeaway:
      'A structured foundation for understanding how cardiac electrical activity is represented on an ECG and how its principal measurements are studied.',
  },
  {
    numeral: 'II',
    accent: 'teal',
    title: 'The Cardiac Rhythm Recognition Atlas',
    headline: 'Learn to Compare Common Cardiac Rhythms Through Visual Patterns and Organized Reference Charts',
    intro: [
      'One of the most challenging parts of ECG education is learning to distinguish rhythm patterns.',
      'Instead of relying exclusively on memorization, this volume organizes common rhythms into categories with visual comparisons and explanations.',
    ],
    items: [
      'Normal sinus rhythm characteristics',
      'Sinus bradycardia and sinus tachycardia',
      'Sinus arrhythmia concepts',
      'Atrial premature complexes',
      'Atrial fibrillation patterns',
      'Atrial flutter patterns',
      'Supraventricular tachycardia concepts',
      'Junctional rhythm fundamentals',
      'Premature ventricular complexes',
      'Ventricular tachycardia patterns',
      'Ventricular fibrillation concepts',
      'Atrioventricular conduction blocks',
      'Rhythm comparison tables',
      'Common rhythm recognition pitfalls',
      'Guided educational rhythm exercises',
    ],
    takeaway:
      'A visual educational atlas for comparing common cardiac rhythm patterns and understanding the characteristics that distinguish them.',
  },
  {
    numeral: 'III',
    accent: 'green',
    title: 'The Step-by-Step ECG Interpretation Workbook',
    headline: 'Practice a Consistent Educational Sequence for Reviewing ECG Tracings',
    intro: [
      'Knowing the names of different rhythms is only one part of learning ECG interpretation.',
      'You also need a way to organize the information presented in a tracing.',
      'This workbook introduces a structured educational sequence that helps you examine individual ECG components and explain your reasoning.',
    ],
    items: [
      'Reviewing ECG recording quality',
      'Organizing heart rate calculations',
      'Evaluating rhythm regularity',
      'Examining P-wave characteristics',
      'Reviewing PR intervals',
      'Examining QRS morphology and duration',
      'Studying ST segments and T waves',
      'Understanding QT interval considerations',
      'Comparing relevant findings',
      'Organizing a preliminary educational interpretation',
      'Identifying incomplete information',
      'Recognizing limitations of simplified rules',
      'Worked interpretation examples',
      'Guided practice worksheets',
      'Self-assessment exercises with explanations',
    ],
    takeaway:
      'A repeatable educational framework for organizing ECG observations and practicing interpretation through structured exercises.',
  },
  {
    numeral: 'IV',
    accent: 'purple',
    title: 'The 12-Lead ECG Interpretation Blueprint',
    headline: 'Understand How the 12 ECG Leads Represent Different Views of Cardiac Electrical Activity',
    intro: [
      'A 12-lead ECG provides information that goes beyond basic rhythm recognition.',
      'However, learning how different leads relate to cardiac anatomy and electrical activity can feel complicated.',
      'This volume organizes the essential concepts into a visual learning pathway.',
    ],
    items: [
      'Introduction to the standard 12-lead ECG',
      'Limb leads and precordial leads',
      'Understanding lead orientation',
      'Cardiac electrical axis fundamentals',
      'Frontal and horizontal plane concepts',
      'Normal waveform progression',
      'R-wave progression concepts',
      'Bundle branch block fundamentals',
      'Introduction to chamber enlargement patterns',
      'ST-segment terminology',
      'T-wave abnormalities',
      'Educational concepts of ischemia and infarction patterns',
      'Understanding contiguous leads',
      'Recognizing technical limitations and artifacts',
      'Guided 12-lead ECG examples',
    ],
    takeaway:
      'An organized educational reference for understanding the major components of 12-lead ECG interpretation and how different lead findings relate to one another.',
  },
  {
    numeral: 'V',
    accent: 'orange',
    title: 'The ECG Interpretation Pitfalls & Clinical Patterns Manual',
    headline: 'Study Common Interpretation Mistakes, Look-Alike Rhythms, and Important ECG Limitations',
    intro: [
      'Not every ECG pattern is straightforward. Some rhythms share similar characteristics. Artifacts can imitate abnormalities. Measurements may vary depending on recording conditions.',
      'And an ECG tracing alone does not always provide enough information to establish a clinical conclusion.',
      'This volume focuses on the concepts that make ECG interpretation more challenging.',
    ],
    items: [
      'Common ECG interpretation misconceptions',
      'Rhythm patterns that may look similar',
      'Understanding ECG artifacts',
      'Lead placement errors and their effects',
      'Common heart rate calculation mistakes',
      'PR and QRS measurement pitfalls',
      'QT interval interpretation limitations',
      'ST-segment interpretation considerations',
      'Distinguishing educational pattern recognition from diagnosis',
      'Paced rhythm fundamentals',
      'Examples involving multiple ECG findings',
      'Understanding the importance of clinical context',
      'Questions to consider when information is incomplete',
      'Visual comparison exercises',
      'Guided interpretation reviews',
    ],
    takeaway:
      'A practical educational reference for recognizing common conceptual mistakes and understanding why ECG findings must be interpreted carefully.',
  },
  {
    numeral: 'VI',
    accent: 'red',
    title: 'The ECG Clinical Case Practice Collection',
    headline: 'Put Your Knowledge Into Practice With Simulated ECG Interpretation Exercises',
    intro: [
      'Reading about ECG interpretation is valuable.',
      'But practicing with different tracings helps you review what you’ve learned and identify areas that deserve more attention.',
      'This volume brings together simulated educational cases designed to reinforce the concepts introduced throughout the collection.',
    ],
    items: [
      'Foundational ECG interpretation exercises',
      'Sinus rhythm practice scenarios',
      'Atrial rhythm educational cases',
      'Junctional rhythm exercises',
      'Ventricular rhythm scenarios',
      'Conduction abnormality examples',
      '12-lead ECG learning cases',
      'ST-segment and T-wave interpretation exercises',
      'Cases involving ECG artifacts',
      'Scenarios with incomplete clinical information',
      'Guided interpretation worksheets',
      'Questions about contextual factors',
      'Suggested educational explanations',
      'Self-assessment questions',
      'Knowledge-gap review exercises',
    ],
    takeaway:
      'A structured opportunity to practice organizing ECG information, explaining your reasoning, and identifying concepts that require additional study.',
    note: 'All cases are educational simulations. They are not validated diagnostic algorithms or substitutes for supervised clinical practice.',
  },
];
